import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import ApiService from '../../services/ApiService';
import Loader from '../../components/Loader';
import AuthService from '../../services/AuthService';
import { useNavigate } from 'react-router-dom';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const defaultTimings = DAYS.map((day) => ({
  day, is_off: false, open: '09:00', close: '18:00',
}));

const PartnerTimings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [timings, setTimings] = useState(defaultTimings);
  const [slotDuration, setSlotDuration] = useState(1);

  useEffect(() => {
    if (!AuthService.getCurrentpartner()) { navigate('/partner/login'); return; }
    getTimings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTimings = async () => {
    try {
      setLoading(true);
      const res = await ApiService.request({ method: 'GET', url: 'partner/getTimings' });
      if (res.data.status) {
        const { timings: saved, slot_duration } = res.data.data;
        setSlotDuration(slot_duration ?? 1);
        if (saved?.length > 0) {
          const map = Object.fromEntries(saved.map((t) => [t.day, t]));
          setTimings(DAYS.map((day) => map[day] || { day, is_off: false, open: '09:00', close: '18:00' }));
        }
      } else {
        toast.error(res.data.message);
      }
    } catch {
      toast.error('Failed to load timings');
    } finally {
      setLoading(false);
    }
  };

  const updateDay = (idx, field, value) => {
    setTimings((prev) => prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const res = await ApiService.request({
        method: 'POST',
        url: 'partner/updateTimings',
        data: { timings, slot_duration: slotDuration },
      });
      if (res.data.status) toast.success(res.data.message || 'Timings saved successfully');
      else toast.error(res.data.message);
    } catch {
      toast.error('Failed to save timings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div className="app-container container-xxl">
          <h1 className="page-heading text-dark fw-bold fs-3 my-0">Business Timings</h1>
        </div>
      </div>

      <div className="app-content flex-column-fluid">
        <div className="app-container container-xxl">
          {loading ? (
            <div className="text-center py-10"><Loader size={35} text="Loading" color="black" /></div>
          ) : (
            <form onSubmit={handleSubmit}>

              {/* Weekly schedule card */}
              <div className="card mb-5">
                <div className="card-header border-0 pt-6">
                  <h3 className="card-title fw-bold fs-4">Weekly Schedule</h3>
                </div>
                <div className="card-body pt-0">
                  {timings.map((t, idx) => (
                    <div
                      key={t.day}
                      className="d-flex align-items-center gap-4 py-3"
                      style={{ borderBottom: idx < timings.length - 1 ? '1px solid #f0f0f0' : 'none' }}
                    >
                      {/* Day label */}
                      <div style={{ minWidth: 110 }}>
                        <span className="fw-semibold fs-6">{t.day}</span>
                      </div>

                      {/* Open/Closed toggle */}
                      <div className="form-check form-switch mb-0" style={{ minWidth: 90 }}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`toggle-${t.day}`}
                          checked={!t.is_off}
                          onChange={(e) => updateDay(idx, 'is_off', !e.target.checked)}
                        />
                        <label
                          className="form-check-label fs-7"
                          htmlFor={`toggle-${t.day}`}
                          style={{ color: t.is_off ? '#999' : '#28a745' }}
                        >
                          {t.is_off ? 'Closed' : 'Open'}
                        </label>
                      </div>

                      {/* Time pickers */}
                      {!t.is_off ? (
                        <div className="d-flex align-items-end gap-3">
                          <div>
                            <label className="fs-7 text-muted mb-1 d-block">Opens</label>
                            <input
                              type="time"
                              className="form-control form-control-solid form-control-sm"
                              style={{ width: 130 }}
                              value={t.open}
                              onChange={(e) => updateDay(idx, 'open', e.target.value)}
                            />
                          </div>
                          <span className="text-muted fs-7 pb-2">to</span>
                          <div>
                            <label className="fs-7 text-muted mb-1 d-block">Closes</label>
                            <input
                              type="time"
                              className="form-control form-control-solid form-control-sm"
                              style={{ width: 130 }}
                              value={t.close}
                              onChange={(e) => updateDay(idx, 'close', e.target.value)}
                            />
                          </div>
                        </div>
                      ) : (
                        <span className="text-muted fst-italic fs-7">Closed all day</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Slot duration card */}
              <div className="card mb-5">
                <div className="card-header border-0 pt-6">
                  <h3 className="card-title fw-bold fs-4">Slot Duration</h3>
                </div>
                <div className="card-body pt-0">
                  <div className="col-md-4">
                    <label className="fs-6 fw-semibold mb-2">Duration (hours)</label>
                    <input
                      type="number"
                      className="form-control form-control-solid"
                      min="0.5"
                      step="0.5"
                      value={slotDuration}
                      onChange={(e) => setSlotDuration(parseFloat(e.target.value) || 0.5)}
                    />
                  </div>
                </div>
              </div>

              <div className="text-end">
                <button
                  type="submit"
                  className="btn btn-sm text-white px-6"
                  style={{ background: '#c4941a' }}
                  disabled={saving}
                >
                  {saving ? <Loader size={18} text="Saving" color="white" /> : 'Save Timings'}
                </button>
              </div>

            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartnerTimings;
